import { useEffect, type MutableRefObject } from 'react'
import { isNonNullable } from 'lib/array/arrayUtils'
import { useOptionalOfferPageAssistantSurfaceRegistryContext } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import { ACCORDION_SECTION_TARGET_IDS } from 'tripPlanner/assistant/surfaces/offerPage/offerPageAccordionContribution'

interface UseNavigableOfferContentFocusTargetsParams {
  /** The rendered accordion sections, in order. Only the local `id` is needed. */
  contentFields: ReadonlyArray<{ id: string }>
  contentGroupRefs: MutableRefObject<Record<string, HTMLDivElement | null>>
  /** Idempotently expands a section (no scroll) — used as the target's prepare step. */
  openSection: (id: string) => void
  scrollOffset: number
}

/**
 * Registers each accordion section that maps to a snapshot target as an assistant focus target,
 * shared across the Flash/Bedbank/Property `Navigable*OfferContent` variants. Each target uses
 * `expand_then_scroll` with an `expand_disclosure` prepare step so a collapsed section is opened
 * before the executor scrolls to it. Sections with no mapped target (e.g. itinerary) are skipped.
 */
export function useNavigableOfferContentFocusTargets({
  contentFields,
  contentGroupRefs,
  openSection,
  scrollOffset,
}: UseNavigableOfferContentFocusTargetsParams): void {
  const registry = useOptionalOfferPageAssistantSurfaceRegistryContext()

  useEffect(() => {
    if (!registry) return undefined

    const unregisters = contentFields
      .map((field) => {
        const targetId = ACCORDION_SECTION_TARGET_IDS[field.id]
        const element = contentGroupRefs.current[field.id]
        if (!targetId || !element) return undefined

        return registry.registerFocusTarget(targetId, {
          element,
          focusMode: 'expand_then_scroll',
          availability: 'rendered',
          prepareSteps: [{
            kind: 'expand_disclosure',
            run: () => {
              openSection(field.id)
              return { status: 'completed' }
            },
          }],
          getScrollOffset: () => scrollOffset,
        })
      })
      .filter(isNonNullable)

    return () => {
      unregisters.forEach((unregister) => unregister())
    }
  }, [registry, contentFields, contentGroupRefs, openSection, scrollOffset])
}
